import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Tooltip } from '../../components';
import * as investorService from '../../services/investor-service';

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

interface PitchCard {
  id: string;
  startupName: string;
  industry: string;
  stage: string;
  description: string;
  askAmount?: string;
  equityOffered?: string;
  traction?: string;
  founderName?: string;
  publishedAt?: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Filter options & visual tokens
// ──────────────────────────────────────────────────────────────────────────

const INDUSTRIES = [
  { value: '', label: 'All Industries' },
  { value: 'Technology', label: 'Technology' },
  { value: 'HealthTech', label: 'HealthTech' },
  { value: 'EdTech', label: 'EdTech' },
  { value: 'Logistics', label: 'Logistics' },
  { value: 'Finance', label: 'Finance' },
  { value: 'CleanTech', label: 'CleanTech' },
  { value: 'AgriTech', label: 'AgriTech' },
];

const STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'idea', label: 'Idea' },
  { value: 'mvp', label: 'MVP' },
  { value: 'growth', label: 'Growth' },
  { value: 'scale', label: 'Scale' },
];

const industryColor: Record<string, string> = {
  Technology: 'bg-chart/10 text-chart border-chart/20',
  HealthTech: 'bg-success/10 text-success border-success/20',
  EdTech: 'bg-role-admin/10 text-role-admin border-role-admin/20',
  Logistics: 'bg-warning/10 text-warning border-warning/20',
  Finance: 'bg-warning/10 text-warning border-warning/20',
  CleanTech: 'bg-chart/10 text-chart border-chart/20',
  AgriTech: 'bg-success/10 text-success border-success/20',
};

const stageLabel: Record<string, string> = {
  idea: 'Idea',
  mvp: 'MVP',
  growth: 'Growth',
  scale: 'Scale',
  'pre-seed': 'Pre-Seed',
  seed: 'Seed',
  'series-a': 'Series A',
};

const isNew = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  return Date.now() - new Date(dateStr).getTime() < 7 * 24 * 60 * 60 * 1000;
};

// ──────────────────────────────────────────────────────────────────────────
// Passed-pitches localStorage helpers (shared with PitchView)
// ──────────────────────────────────────────────────────────────────────────

const PASSED_KEY = 'investor_passed_pitches';

const readPassed = (): Set<string> => {
  try {
    const raw = localStorage.getItem(PASSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const writePassed = (s: Set<string>): void => {
  try {
    localStorage.setItem(PASSED_KEY, JSON.stringify(Array.from(s)));
  } catch {}
};

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

const InvestorDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [pitches, setPitches] = useState<PitchCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [industryFilter, setIndustryFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [passedIds, setPassedIds] = useState<Set<string>>(() => readPassed());

  useEffect(() => {
    investorService
      .getPublishedPitches()
      .then((data) => {
        setPitches(
          data.map((p) => ({
            id: p._id,
            startupName: p.startupId?.name || 'Unknown',
            industry: p.startupId?.industry || 'Other',
            stage: p.startupId?.stage || 'mvp',
            description: p.problem || '',
            askAmount: p.askAmount,
            equityOffered: p.equityOffered,
            traction: p.traction,
            founderName: p.userId?.name,
            publishedAt: p.updatedAt || p.createdAt,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const unpassPitch = useCallback((id: string) => {
    setPassedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      writePassed(next);
      return next;
    });
  }, []);

  const filtered = pitches
    .filter((p) => !passedIds.has(p.id))
    .filter((p) => !industryFilter || p.industry === industryFilter)
    .filter((p) => !stageFilter || p.stage === stageFilter);

  const passedPitches = pitches.filter((p) => passedIds.has(p.id));

  const newCount = pitches.filter((p) => !passedIds.has(p.id) && isNew(p.publishedAt)).length;
  const industriesShown = new Set(filtered.map((p) => p.industry)).size;

  return (
    <div className="space-y-10 pb-10">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-2">
        <div>
          <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Investor Portal</p>
          <h1 className="font-display text-5xl tracking-wide text-text leading-none">Deal Flow.</h1>
          <p className="font-serif-display italic text-muted text-lg mt-2">
            Vetted startups from the BunyadKar accelerator, approved by mentors.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:pb-1">
          <Tooltip content="Total published pitches available to review">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart tabular-nums">{loading ? '–' : filtered.length}</span>
              <span className="text-muted">In Deal Flow</span>
            </span>
          </Tooltip>
          {newCount > 0 && (
            <Tooltip content="Pitches published in the last 7 days">
              <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
                <span className="w-1.5 h-1.5 rounded-full bg-chart animate-pulse" />
                <span className="font-bold text-chart tabular-nums">{newCount}</span>
                <span className="text-muted">New This Week</span>
              </span>
            </Tooltip>
          )}
          <Tooltip content="Number of distinct industries represented">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-success tabular-nums">{loading ? '–' : industriesShown}</span>
              <span className="text-muted">Industries</span>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ─── Filters ─── */}
      <div className="flex items-center gap-3 flex-wrap">
        <p className="font-display text-xs tracking-display-sm text-muted uppercase">Filter</p>
        <div className="w-44">
          <Select name="industry" options={INDUSTRIES} value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)} />
        </div>
        <div className="w-36">
          <Select name="stage" options={STAGES} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} />
        </div>
        {(industryFilter || stageFilter) && (
          <button
            onClick={() => {
              setIndustryFilter('');
              setStageFilter('');
            }}
            className="text-xs text-muted hover:text-text underline transition-colors"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-xs text-muted tabular-nums">
          {loading ? '—' : `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* ─── Grid of pitch cards ─── */}
      {loading ? (
        <p className="font-serif-display italic text-muted">Loading deal flow…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-shell border border-line rounded-card p-12 text-center">
          <p className="font-display text-lg tracking-wide text-muted uppercase">No Matches</p>
          <p className="font-serif-display italic text-muted text-sm mt-1">
            {passedPitches.length > 0
              ? 'All pitches matching your filters have been passed. Try restoring some below.'
              : 'Try adjusting your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((pitch, i) => (
            <button
              key={pitch.id}
              onClick={() => navigate(`/app/investor/pitches/${pitch.id}`)}
              className="group min-h-[340px] text-left bg-shell border border-line rounded-card p-5 hover:border-chart/40 hover:shadow-lg transition-all animate-fade-in-up flex flex-col"
              style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}
            >
              {/* Top row: avatar + pills */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-chart/10 border border-chart/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-chart font-bold text-base leading-none">
                    {pitch.startupName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 justify-end">
                  {isNew(pitch.publishedAt) && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-chart text-black tracking-wide uppercase">
                      New
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                      industryColor[pitch.industry] || 'bg-surface text-muted border-line'
                    }`}
                  >
                    {pitch.industry}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line capitalize">
                    {stageLabel[pitch.stage] || pitch.stage}
                  </span>
                </div>
              </div>

              {/* Name */}
              <h3 className="font-display text-xl md:text-2xl tracking-wide text-text leading-tight mb-3">
                {pitch.startupName}
              </h3>

              {/* One-liner */}
              <p className="font-serif-display italic text-muted text-sm leading-relaxed line-clamp-3 mb-4 flex-1">
                {pitch.description}
              </p>

              {/* Traction */}
              {pitch.traction && (
                <div className="mb-4 pb-4 border-b border-line">
                  <p className="text-xs text-chart font-medium flex items-start gap-1.5 line-clamp-2">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
                      />
                    </svg>
                    {pitch.traction}
                  </p>
                </div>
              )}

              {/* Metric band — ASK + EQUITY */}
              <div className="bg-accent-soft border border-chart/20 rounded-xl p-4 grid grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="text-[9px] font-semibold uppercase tracking-widest text-chart mb-1">Ask</div>
                  <div className="font-display text-xl md:text-2xl text-text tabular-nums leading-none">
                    {pitch.askAmount || '—'}
                  </div>
                </div>
                <div className="border-l border-chart/20 pl-3">
                  <div className="text-[9px] font-semibold uppercase tracking-widest text-chart mb-1">Equity</div>
                  <div className="font-display text-xl md:text-2xl text-text tabular-nums leading-none">
                    {pitch.equityOffered || '—'}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between">
                {pitch.founderName ? (
                  <span className="text-[11px] text-muted truncate">by {pitch.founderName}</span>
                ) : (
                  <span />
                )}
                <span className="inline-flex items-center gap-1 text-chart text-xs font-medium group-hover:gap-2 transition-all">
                  View pitch
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ─── Previously passed ─── */}
      {passedPitches.length > 0 && (
        <div className="border-t border-line pt-6">
          <div className="flex items-center gap-2 mb-3">
            <p className="font-display text-xs tracking-display text-muted uppercase">Previously Passed</p>
            <span className="text-xs text-muted tabular-nums">({passedPitches.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {passedPitches.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-2 bg-surface border border-line rounded-full px-3 py-1.5 text-xs"
              >
                <span className="text-muted line-through">{p.startupName}</span>
                <button
                  type="button"
                  onClick={() => unpassPitch(p.id)}
                  className="text-chart hover:text-chart/80 font-medium transition-colors"
                  aria-label={`Restore ${p.startupName}`}
                >
                  Undo
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorDashboard;
